/* eslint-disable consistent-return */
import { nextTick, onMounted, getCurrentInstance } from "vue";
import { useRoute, onBeforeRouteLeave } from "vue-router";
import getScrollTarget from "@/v2/shared/getScrollTarget.js";

const storagePrefix = "scroll-position";

export default function useScrollPersistence() {
  function storageKeyFromPath(path, customPrefixKey) {
    if (customPrefixKey) {
      return `${storagePrefix}:${customPrefixKey}:${path}`;
    }

    return `${storagePrefix}:${path}`;
  }

  function saveScrollPosition(path, position, customPrefixKey) {
    window.localStorage.setItem(
      storageKeyFromPath(path, customPrefixKey),
      position
    );
  }

  function retrieveScrollPosition(path, customPrefixKey) {
    const persistedValue = window.localStorage.getItem(
      storageKeyFromPath(path, customPrefixKey)
    );

    if (!persistedValue) {
      return null;
    }

    return persistedValue;
  }

  function clearAllScrollPositions() {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(storagePrefix))
      .forEach((item) => localStorage.removeItem(item));
  }

  function handleAfterEachRouteChange(routeTo) {
    if (routeTo.name !== "Home") {
      return;
    }

    clearAllScrollPositions();
  }

  function isSwiperContainer(container) {
    return "__swiper__" in container;
  }

  function handleScrollPersistence(
    scrollContainer,
    direction,
    customPrefixKey
  ) {
    // prevent errors in Storybook
    if (!getCurrentInstance().appContext.config.globalProperties.$router) {
      return {
        restorePosition: () => {},
      };
    }

    const attribute = direction === "horizontal" ? "scrollLeft" : "scrollTop";
    const route = useRoute();

    function restorePosition() {
      const container = getScrollTarget(scrollContainer);

      if (!container) {
        return;
      }

      const currentPath = route.path;
      const position = retrieveScrollPosition(currentPath, customPrefixKey);

      nextTick(() => {
        if (isSwiperContainer(container)) {
          container.slideTo(position ?? 0);
        } else {
          container[attribute] = position ?? 0;
        }
      });
    }

    function persistPosition() {
      const container = getScrollTarget(scrollContainer);

      if (!container) {
        return;
      }

      const currentPath = route.path;
      const position = isSwiperContainer(container)
        ? container.activeIndex ?? 0
        : container[attribute] ?? 0;

      saveScrollPosition(currentPath, position, customPrefixKey);
    }

    onMounted(restorePosition);
    onBeforeRouteLeave(persistPosition);

    return {
      restorePosition,
    };
  }

  return {
    handleAfterEachRouteChange,
    handleScrollPersistence,
  };
}
